import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import client from "../../helpers/Client";

const RED = "#b00000";
const INOHOM_LOGO_URL = "https://i.hizliresim.com/40dvsvk.png";

export default function ServiceFormDownload() {
  const { token } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setIsLoading(true);
      setError("");
      try {
        const { data } = await client.get(`/public/service_forms/${encodeURIComponent(token)}/`);
        if (!alive) return;
        setForm(data || null);
      } catch (e) {
        if (!alive) return;
        const detail = e?.response?.data?.detail || e?.message || "Form alınamadı";
        setError(String(detail));
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const opList = useMemo(() => {
    const ops = form?.operation_types && typeof form.operation_types === "object" ? form.operation_types : {};
    return Object.entries(ops).filter(([_, v]) => !!v).map(([k]) => k);
  }, [form]);

  const handlePdf = async () => {
    const element = document.getElementById("public-form-print-area");
    if (!element || isExporting) return;
    setIsExporting(true);
    try {
      const serial = String(form?.serial_number || "").trim() || "servis_formu";
      const opt = {
        margin: 10,
        filename: `${serial}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0, windowWidth: 1024, letterRendering: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().from(element).set(opt).save();
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ background: "white", borderRadius: "12px", padding: "16px 18px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", fontWeight: 800 }}>
          Yükleniyor...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ background: "white", borderRadius: "12px", padding: "18px 18px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", maxWidth: "720px", width: "100%" }}>
          <div style={{ fontWeight: 900, fontSize: "18px", marginBottom: "8px", color: RED }}>Form görüntülenemedi</div>
          <div style={{ color: "#444", fontSize: "14px" }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "14px" }}>
      <div style={{ maxWidth: "980px", margin: "0 auto 12px auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img src={INOHOM_LOGO_URL} alt="inohom" style={{ height: "34px", objectFit: "contain", display: "block" }} />
          <div>
            <div style={{ fontWeight: 900 }}>Servis Formu</div>
            <div style={{ fontSize: "12px", color: "#666" }}>{form?.serial_number ? `Seri: ${form.serial_number}` : ""}</div>
          </div>
        </div>
        <button
          onClick={handlePdf}
          disabled={isExporting}
          style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "10px", padding: "10px 14px", cursor: isExporting ? "not-allowed" : "pointer", fontWeight: 900 }}
        >
          {isExporting ? "Hazırlanıyor..." : "PDF İndir"}
        </button>
      </div>

      <div
        id="public-form-print-area"
        style={{
          background: "white",
          maxWidth: "980px",
          margin: "0 auto",
          padding: "14px",
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          fontFamily: "Arial, sans-serif",
          color: "#111",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          <div>
            <div style={{ fontSize: "12px", color: "#666" }}>Müşteri</div>
            <div style={{ fontWeight: 900 }}>{form?.customer_name || "-"}</div>
            <div style={{ fontSize: "13px", color: "#333", marginTop: "4px" }}>{form?.customer_phone || ""}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#666" }}>Proje</div>
            <div style={{ fontWeight: 900 }}>{form?.project_name || "-"}</div>
            <div style={{ fontSize: "13px", color: "#333", marginTop: "4px" }}>{form?.service_date || ""}</div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ fontSize: "12px", color: "#666" }}>Adres</div>
            <div style={{ fontSize: "13px" }}>{form?.customer_address || "-"}</div>
          </div>
        </div>

        <div style={{ borderTop: `2px solid ${RED}`, marginTop: "12px", paddingTop: "10px" }}>
          <div style={{ fontSize: "12px", color: "#666" }}>Servis Nedeni</div>
          <div style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>{form?.service_reason || "-"}</div>
        </div>

        <div style={{ borderTop: `2px solid ${RED}`, marginTop: "12px", paddingTop: "10px" }}>
          <div style={{ fontSize: "12px", color: "#666" }}>Yapılan İşlem</div>
          <div style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>{form?.work_done || "-"}</div>
        </div>

        <div style={{ borderTop: `2px solid ${RED}`, marginTop: "12px", paddingTop: "10px" }}>
          <div style={{ fontSize: "12px", color: "#666" }}>İşlem Tipleri</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
            {opList.length ? opList.map((x) => (
              <span key={x} style={{ fontSize: "12px", border: "1px solid #e5e7eb", borderRadius: "999px", padding: "4px 10px", background: "#fafafa" }}>
                {x}
              </span>
            )) : <span style={{ fontSize: "13px" }}>-</span>}
          </div>
        </div>

        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "14px", paddingTop: "10px", display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
          <div style={{ fontSize: "12px", color: "#666" }}>inohom.com</div>
          <div style={{ fontSize: "12px", color: "#666" }}>GST Elektronik A.Ş.</div>
        </div>
      </div>
    </div>
  );
}

